import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Filter, X, Calendar, MapPin, User, Settings, ChevronDown, Upload, Download, CheckCircle, Clock, AlertTriangle, ArrowUpDown, ChevronRight } from 'lucide-react';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => clearTimeout(timeout);
  return debounced;
};

// Memoized Notes Field Component - prevents re-renders while typing
const NotesField = React.memo(({ pmId, initialNotes, onSave, isMobile }) => {
  const textareaRef = useRef(null);
  
  return (
    <textarea
      ref={textareaRef}
      defaultValue={initialNotes || ''}
      onBlur={(e) => onSave(pmId, e.target.value)}
      placeholder="Enter notes..."
      style={{
        width: '100%',
        minHeight: isMobile ? '80px' : '60px',
        padding: isMobile ? '12px' : '8px',
        border: '1px solid #d1d5db',
        borderRadius: isMobile ? '6px' : '4px',
        fontSize: isMobile ? '14px' : '12px',
        resize: 'vertical'
      }}
    />
  );
}, (prevProps, nextProps) => {
  // Only re-render if pmId changes (component should never change for same PM)
  return prevProps.pmId === nextProps.pmId;
});

const GoodShepherdPMs = ({ onCountsChange, user }) => {
  // Existing state management (unchanged)
  const [searchState, setSearchState] = useState({
    q: '',
    status: 'open',
    location: '',
    assignee: '',
    equipment_type: '',
    date_from: '',
    date_to: '',
    adhesive_cleaned: '',
    closed_month: '',
    sort_by: 'scheduled_time',
    sort_order: 'asc',
    page: 1,
    limit: 20
  });

  const [pms, setPms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [statusCounts, setStatusCounts] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    assignees: [],
    equipment_types: []
  });

  // Action states
  const [completingPMs, setCompletingPMs] = useState(new Set());
  const [processingPMs, setProcessingPMs] = useState(new Set());

  // Adhesive-question modal state (Mark Complete no longer uses window.confirm).
  const [completeModalPm, setCompleteModalPm] = useState(null);
  const [adhesiveChoice, setAdhesiveChoice] = useState('');
  const [adhesivePrompt, setAdhesivePrompt] = useState(false);

  // CSV Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({ type: '', message: '' });
  const [uploadResults, setUploadResults] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  // Mobile state
  const [isMobile, setIsMobile] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Status options for quick filters
  const statusOptions = [
    { value: 'open', label: 'All Open', key: 'open' },
    { value: 'current-month', label: 'This Month', key: 'current-month' },
    { value: 'overdue', label: 'Overdue', key: 'overdue' },
    { value: 'in-progress', label: 'In Progress', key: 'in-progress' },
    { value: 'assigned', label: 'Assigned', key: 'assigned' },
    { value: 'completed', label: 'Completed', key: 'completed' },
    { value: 'processed', label: 'Processed', key: 'processed' }
  ];

const handleCSVUpload = async () => {
  if (!uploadFile) {
    setUploadStatus({ type: 'error', message: 'Please select a file' });
    return;
  }

  setUploading(true);
  setUploadStatus({ type: '', message: '' });
  setUploadResults(null);

  try {
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    const response = await fetch('/api/gs-pms/upload', {
      method: 'POST',
      body: formData
    });

    const result = await response.json();
    
    if (result.success) {
      setUploadResults({
        total: result.total || 0,
        inserted: result.inserted || 0,
        updated: result.updated || 0,
        skipped: result.skipped || 0,
        deleted: result.deleted || 0,
        preserved: result.preserved || 0,
        errors: result.errors || 0
      });
      
      setUploadStatus({ 
        type: 'success', 
        message: `Upload complete! Inserted: ${result.inserted}, Updated: ${result.updated || 0}, Unchanged: ${result.skipped}, Removed (closed in TeamChamps): ${result.deleted || 0}${result.preserved > 0 ? `, Kept (completed here): ${result.preserved}` : ''}${result.errors > 0 ? `, Errors: ${result.errors}` : ''}` 
      });
      
      // Refresh after 3 seconds
      setTimeout(() => {
        searchPMs(searchState);
        fetchStatusCounts();
        if (onCountsChange) onCountsChange();
        setShowUpload(false);
        setUploadStatus({ type: '', message: '' });
        setUploadResults(null);
        setUploadFile(null);
      }, 3000);
    } else {
      setUploadStatus({ 
        type: 'error', 
        message: result.error || 'Upload failed' 
      });
    }
  } catch (error) {
    console.error('Upload error:', error);
    setUploadStatus({ 
      type: 'error', 
      message: 'Upload failed: ' + error.message 
    });
  } finally {
    setUploading(false);
  }
};

  // Unified search function (unchanged)
  const searchPMs = useCallback(
    debounce(async (params) => {
      setLoading(true);
      try {
        const apiParams = {
          q: params.q || '',
          status: params.status || 'all',
          location: params.location || '',
          assignee: params.assignee || '',
          equipment_type: params.equipment_type || '',
          date_from: params.date_from || '',
          date_to: params.date_to || '',
          adhesive_cleaned: params.adhesive_cleaned || '',
          closed_month: params.closed_month || '',
          sort_by: params.sort_by || 'scheduled_time',
          sort_order: params.sort_order || 'asc',
          page: params.page || 1,
          limit: params.limit || 20
        };
        
        const searchParams = new URLSearchParams(apiParams);
        const response = await fetch(`/api/gs-pms/search?${searchParams}`);
        const data = await response.json();

        if (data.success) {
          setPms(data.data);
          setPagination(data.pagination);
          setFilterOptions(data.filter_options);
        } else {
          console.error('Search failed:', data.error);
          setPms([]);
        }
      } catch (error) {
        console.error('Search error:', error);
        setPms([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  // Fetch status counts (unchanged)
  const fetchStatusCounts = useCallback(async () => {
    try {
      const response = await fetch('/api/gs-pms/search/counts');
      const data = await response.json();
      if (data.success) {
        setStatusCounts(data.counts);
      }
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  }, []);

  // On mount: consume any pending drill-down filter dropped by DashboardHome
  // via sessionStorage. Key is single-use (removed on read). Whatever fields
  // are set here get merged into searchState, which triggers the search
  // effect below.
  useEffect(() => {
    const raw = sessionStorage.getItem('pmplus_pending_pm_filter');
    if (!raw) return;
    sessionStorage.removeItem('pmplus_pending_pm_filter');
    try {
      const f = JSON.parse(raw);
      setSearchState(prev => ({
        ...prev,
        adhesive_cleaned: typeof f.adhesive_cleaned === 'string' ? f.adhesive_cleaned : prev.adhesive_cleaned,
        closed_month: typeof f.closed_month === 'string' ? f.closed_month : prev.closed_month,
        status: typeof f.status === 'string' ? f.status : prev.status,
        page: 1
      }));
    } catch (_) { /* ignore malformed */ }
  }, []);

  // Effects (unchanged)
  useEffect(() => {
    searchPMs(searchState);
  }, [searchState, searchPMs]);

  useEffect(() => {
    fetchStatusCounts();
  }, [fetchStatusCounts]);

  // Helper functions (unchanged)
  const updateSearchState = (updates) => {
    setSearchState(prev => ({
      ...prev,
      ...updates,
      page: updates.page !== undefined ? updates.page : 1
    }));
  };

  const handleStatusFilter = (status) => {
    updateSearchState({ status, page: 1 });
  };

  const handleSearchChange = (query) => {
    updateSearchState({ q: query, page: 1 });
  };

  const handlePageChange = (newPage) => {
    updateSearchState({ page: newPage });
  };

  const clearAllFilters = () => {
    setSearchState({
      q: '',
      status: 'open',
      location: '',
      assignee: '',
      equipment_type: '',
      date_from: '',
      date_to: '',
      sort_by: 'scheduled_time',
      sort_order: 'asc',
      page: 1,
      limit: 20
    });
    setShowAdvancedSearch(false);
  };

  // Mark Complete now runs through a modal to capture the required
  // adhesive-residue answer. openCompleteModal is the button handler;
  // submitCompleteModal is the modal's Confirm handler.
  const openCompleteModal = (pm) => {
    setCompleteModalPm(pm);
    setAdhesiveChoice('');
    setAdhesivePrompt(false);
  };

  const closeCompleteModal = () => {
    setCompleteModalPm(null);
    setAdhesiveChoice('');
    setAdhesivePrompt(false);
  };

  const submitCompleteModal = async () => {
    if (!completeModalPm) return;
    if (!adhesiveChoice) {
      setAdhesivePrompt(true);
      return;
    }
    const pm = completeModalPm;
    setCompletingPMs(prev => new Set(prev).add(pm.id));
    try {
      const response = await fetch(`/api/gs-pms/${pm.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adhesive_cleaned: adhesiveChoice })
      });

      const result = await response.json();
      if (result.success) {
        closeCompleteModal();
        searchPMs(searchState);
        fetchStatusCounts();
        if (onCountsChange) onCountsChange();
      } else {
        alert('Failed to complete PM: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Complete PM error:', error);
      alert('Failed to complete PM: ' + error.message);
    } finally {
      setCompletingPMs(prev => {
        const newSet = new Set(prev);
        newSet.delete(pm.id);
        return newSet;
      });
    }
  };

  const processPM = async (pmId, pmNumber) => {
    if (!window.confirm(`Mark PM ${pmNumber} as processed?`)) return;

    setProcessingPMs(prev => new Set(prev).add(pmId));
    try {
      const response = await fetch(`/api/gs-pms/${pmId}/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user?.id,
          userName: user?.fullName
        })
      });

      const result = await response.json();
      if (result.success) {
        searchPMs(searchState);
        fetchStatusCounts();
        if (onCountsChange) onCountsChange();
      } else {
        alert('Failed to process PM: ' + result.error);
      }
    } catch (error) {
      console.error('Process PM error:', error);
      alert('Failed to process PM: ' + error.message);
    } finally {
      setProcessingPMs(prev => {
        const newSet = new Set(prev);
        newSet.delete(pmId);
        return newSet;
      });
    }
  };

  const handleUpdateNotes = useCallback(async (pmId, notes) => {
    try {
      const response = await fetch(`/api/gs-pms/${pmId}/notes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: notes,
          userId: user?.id,
          userName: user?.fullName
        })
      });

      const result = await response.json();
      if (result.success) {
        // Update local state
        setPms(prevPms =>
          prevPms.map(pm =>
            pm.id === pmId ? { ...pm, notes: notes } : pm
          )
        );
      } else {
        alert('Failed to save notes: ' + result.error);
      }
    } catch (error) {
      console.error('Save notes error:', error);
      alert('Failed to save notes: ' + error.message);
    }
  }, [user]);

  // Utility functions (unchanged)
  const getStatusIcon = (state, complete) => {
    if (complete === 'YES') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (state === 'Work in Progress') return <Clock className="w-4 h-4 text-blue-500" />;
    if (state === 'Assigned') return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return <Clock className="w-4 h-4 text-gray-400" />;
  };

  const isOverdue = (scheduledTime) => {
    if (!scheduledTime) return false;
    const [month, day, year] = scheduledTime.split('-');
    const pmDate = new Date(year, month - 1, day);
    const today = new Date();
    return pmDate < today;
  };

  // Count active filters for display
  const activeFilterCount = Object.entries(searchState).filter(([key, value]) => {
    if (key === 'page' || key === 'limit' || key === 'sort_by' || key === 'sort_order') return false;
    if (key === 'status' && value === 'all') return false;
    return value && value.toString().trim() !== '';
  }).length;

  // Mobile Card Component
  const PMCard = ({ pm }) => {
    const isExpanded = expandedCard === pm.id;
    const canComplete = searchState.status !== 'processed' && pm.complete !== 'YES';
    const canProcess = searchState.status === 'completed' && pm.processed !== 'YES';
    
    return (
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        marginBottom: '12px',
        overflow: 'hidden'
      }}>
       {/* Card Header - Always Visible */}
        <div 
          style={{
            padding: '16px',
            borderBottom: isExpanded ? '1px solid #f3f4f6' : 'none',
            cursor: 'pointer'
          }}
          onClick={() => setExpandedCard(isExpanded ? null : pm.id)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: '600', 
                color: '#2563eb',
                marginBottom: '4px'
              }}>
                {pm.asset || 'N/A'}
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#374151',
                fontWeight: '500',
                marginBottom: '4px'
              }}>
                {pm.type}{pm.pm_frequency ? ` • ${pm.pm_frequency}` : ''}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: '#6b7280'
              }}>
                {pm.work_location}{pm.vicinity ? ` • ${pm.vicinity}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
              {getStatusIcon(pm.state, pm.complete)}
              <ChevronRight 
                size={16} 
                style={{ 
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  color: '#9ca3af'
                }} 
              />
            </div>
          </div>
          
          {/* Key Info - Always Visible */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
            <div>
              <span style={{ color: '#6b7280' }}>Work Order: </span>
              <span style={{ fontWeight: '500' }}>{pm.number}</span>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ 
                fontWeight: '500',
                color: isOverdue(pm.scheduled_time) && pm.complete !== 'YES' ? '#dc2626' : '#374151'
              }}>
                {pm.scheduled_time}
              </span>
            </div>
          </div>
        </div>
        {/* Expanded Details */}
        {isExpanded && (
          <div style={{ padding: '16px' }}>
            {/* Equipment & Location */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                <span style={{ color: '#6b7280' }}>Equipment: </span>
                <span style={{ fontWeight: '500' }}>{pm.type}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                {pm['asset.asset_manufacturer']} {pm.model}
              </div>
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                <span style={{ color: '#6b7280' }}>Location: </span>
                <span style={{ fontWeight: '500' }}>{pm.work_location}</span>
              </div>
              {pm.vicinity && (
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  {pm.vicinity}
                </div>
              )}
            </div>

            {/* Assignee & Risk */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Assigned To</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  {pm.assigned_to || <span style={{ color: '#9ca3af' }}>Unassigned</span>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Risk Level</div>
                {pm.risk_level ? (
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background: pm.risk_level >= 18 ? '#fef2f2' : pm.risk_level >= 15 ? '#fef3c7' : pm.risk_level >= 10 ? '#dcfce7' : '#f3f4f6',
                    color: pm.risk_level >= 18 ? '#dc2626' : pm.risk_level >= 15 ? '#d97706' : pm.risk_level >= 10 ? '#15803d' : '#6b7280'
                  }}>
                    {pm.risk_level}
                  </span>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>N/A</span>
                )}
              </div>
            </div>

            {/* Frequency */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Frequency</div>
              {pm.pm_frequency ? (
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '500',
                  background: '#eff6ff',
                  color: '#1e40af'
                }}>
                  {pm.pm_frequency}
                </span>
              ) : (
                <span style={{ color: '#9ca3af', fontSize: '12px' }}>N/A</span>
              )}
            </div>

            {/* Status */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Status</div>
              <span style={{
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                background: pm.complete === 'YES' ? '#dcfce7' : 
                           pm.state === 'Work in Progress' ? '#dbeafe' :
                           pm.state === 'Assigned' ? '#fef3c7' : '#f3f4f6',
                color: pm.complete === 'YES' ? '#15803d' :
                      pm.state === 'Work in Progress' ? '#1e40af' :
                      pm.state === 'Assigned' ? '#d97706' : '#6b7280'
              }}>
                {pm.complete === 'YES' ? 'Completed' : pm.state}
              </span>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Notes</div>
              <NotesField 
                pmId={pm.id}
                initialNotes={pm.notes}
                onSave={handleUpdateNotes}
                isMobile={true}
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {canProcess ? (
                <button
                  onClick={() => processPM(pm.id, pm.number)}
                  disabled={processingPMs.has(pm.id)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: processingPMs.has(pm.id) ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: processingPMs.has(pm.id) ? 'not-allowed' : 'pointer',
                    opacity: processingPMs.has(pm.id) ? 0.6 : 1
                  }}
                >
                  {processingPMs.has(pm.id) ? 'Processing...' : 'Mark as Processed'}
                </button>
              ) : canComplete ? (
                <button
                  onClick={() => openCompleteModal(pm)}
                  disabled={completingPMs.has(pm.id)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: completingPMs.has(pm.id) ? '#9ca3af' : '#16a34a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: completingPMs.has(pm.id) ? 'not-allowed' : 'pointer',
                    opacity: completingPMs.has(pm.id) ? 0.6 : 1
                  }}
                >
                  {completingPMs.has(pm.id) ? 'Completing...' : 'Mark Complete'}
                </button>
              ) : (
                <div style={{ 
                  flex: 1,
                  padding: '12px 16px',
                  textAlign: 'center',
                  color: '#16a34a', 
                  fontSize: '14px', 
                  fontWeight: '500',
                  background: '#f0f9ff',
                  borderRadius: '6px'
                }}>
                  ✓ Complete
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // CSV Upload Modal - MUST come before main return
  if (showUpload) {
    return (
      <div style={{ 
        position: 'fixed', 
        top: 0, 
        left: 0, 
        right: 0, 
        bottom: 0, 
        background: 'rgba(0,0,0,0.5)', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        zIndex: 1000,
        padding: '16px'
      }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          padding: '24px', 
          maxWidth: '600px', 
          width: '100%',
          maxHeight: '90vh', 
          overflow: 'auto' 
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px' 
          }}>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              Upload PM CSV
            </h3>
            <button 
              onClick={() => {
                setShowUpload(false);
                setUploadFile(null);
                setUploadStatus({ type: '', message: '' });
                setUploadResults(null);
              }} 
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={24} />
            </button>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151'
            }}>
              Select CSV File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                setUploadFile(e.target.files[0]);
                setUploadStatus({ type: '', message: '' });
                setUploadResults(null);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
            {uploadFile && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>
                Selected: {uploadFile.name}
              </div>
            )}
          </div>

          {uploadResults && (
            <div style={{
              padding: '16px',
              borderRadius: '6px',
              marginBottom: '16px',
              background: '#f3f4f6',
              border: '1px solid #d1d5db'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                Upload Summary
              </div>
              <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>Total Records:</span>
                  <span style={{ fontWeight: '600' }}>{uploadResults.total}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#16a34a' }}>✓ Inserted:</span>
                  <span style={{ fontWeight: '600', color: '#16a34a' }}>{uploadResults.inserted}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#d97706' }}>⊘ Skipped:</span>
                  <span style={{ fontWeight: '600', color: '#d97706' }}>{uploadResults.skipped}</span>
                </div>
                {uploadResults.errors > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#dc2626' }}>✗ Errors:</span>
                    <span style={{ fontWeight: '600', color: '#dc2626' }}>{uploadResults.errors}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {uploadStatus.message && (
            <div style={{
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              background: uploadStatus.type === 'success' ? '#dcfce7' : '#fef2f2',
              color: uploadStatus.type === 'success' ? '#15803d' : '#dc2626',
              fontSize: '14px',
              border: `1px solid ${uploadStatus.type === 'success' ? '#86efac' : '#fca5a5'}`
            }}>
              {uploadStatus.message}
            </div>
          )}

          <div style={{
            padding: '12px',
            background: '#eff6ff',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#1e40af',
            border: '1px solid #bfdbfe'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>
              CSV Format Requirements:
            </div>
            <ul style={{ margin: '0', paddingLeft: '20px', lineHeight: '1.6' }}>
              <li>Must include work order numbers</li>
              <li>Existing PMs are refreshed from the CSV; your notes and completions are kept</li>
              <li>Date format: MM-DD-YYYY</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleCSVUpload}
              disabled={!uploadFile || uploading}
              style={{
                flex: 1,
                padding: '10px 16px',
                background: (!uploadFile || uploading) ? '#9ca3af' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: (!uploadFile || uploading) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Upload size={16} />
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </button>
            <button
              onClick={() => {
                setShowUpload(false);
                setUploadFile(null);
                setUploadStatus({ type: '', message: '' });
                setUploadResults(null);
              }}
              disabled={uploading}
              style={{
                padding: '10px 16px',
                background: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: uploading ? 'not-allowed' : 'pointer',
                opacity: uploading ? 0.5 : 1
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main component return starts here
  return (
    <div style={{ padding: isMobile ? '16px' : '24px' }}>
      {/* Hospital banner. Deliberately amber, not the app's NRMC blue: these
          pages sit next to the NRMC ones in the same sidebar, and a CSV
          uploaded on the wrong page would reconcile against the wrong
          hospital's table. The colour is the at-a-glance guard. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        background: '#fffbeb', border: '1px solid #fcd34d', borderLeft: '5px solid #f59e0b',
        borderRadius: 8, padding: '0.6rem 0.9rem', marginBottom: '1.25rem',
        color: '#92400e', fontSize: '0.88rem', fontWeight: 600,
      }}>
        <span style={{ fontSize: '1rem' }}>&#9873;</span>
        CHRISTUS GOOD SHEPHERD &mdash; separate hospital. This page never touches NRMC data.
      </div>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        marginBottom: '24px',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '16px' : '0'
      }}>
        <div>
          <h1 style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: '700', margin: '0 0 8px 0', color: '#1f2937' }}>
            Good Shepherd PMs
          </h1>
          <p style={{ color: '#6b7280', margin: 0, fontSize: isMobile ? '14px' : '16px' }}>
            Preventive maintenance for Christus Good Shepherd Hospital
          </p>
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowUpload(true)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '8px 16px', 
                background: '#2563eb', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px', 
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <Upload size={16} />
              Upload CSV
            </button>
            <button style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px', 
              padding: '8px 16px', 
              border: '1px solid #d1d5db', 
              borderRadius: '6px', 
              background: 'white', 
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <Download size={16} />
              Export
            </button>
          </div>
        )}
      </div>

      {/* Rest of your existing JSX continues here... */}
  

      {/* Main Search Bar */}
      <div style={{ 
        background: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        border: '1px solid #e5e7eb', 
        padding: '16px', 
        marginBottom: '24px' 
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ flex: 1, position: 'relative', width: isMobile ? '100%' : 'auto' }}>
            <Search style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)', 
              color: '#9ca3af' 
            }} size={20} />
            <input
              type="text"
              placeholder="Search by asset number, location, or equipment type..."
              value={searchState.q}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: isMobile ? '12px' : '8px',
                paddingBottom: isMobile ? '12px' : '8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            style={{
              padding: isMobile ? '12px 16px' : '8px 16px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              background: showAdvancedSearch || activeFilterCount > 0 ? '#dbeafe' : 'white',
              color: showAdvancedSearch || activeFilterCount > 0 ? '#1e40af' : '#374151',
              width: isMobile ? '100%' : 'auto',
              justifyContent: isMobile ? 'center' : 'flex-start'
            }}
          >
            <Filter size={16} />
            Advanced Filters
            {activeFilterCount > 0 && (
              <span style={{
                background: '#3b82f6',
                color: 'white',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '700'
              }}>
                {activeFilterCount}
              </span>
            )}
            <ChevronDown size={16} style={{ 
              transform: showAdvancedSearch ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }} />
          </button>
        </div>

        {/* Advanced Search Panel */}
        {showAdvancedSearch && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <Calendar size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Date From
                </label>
                <input
                  type="date"
                  value={searchState.date_from}
                  onChange={(e) => updateSearchState({ date_from: e.target.value })}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px' : '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <Calendar size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Date To
                </label>
                <input
                  type="date"
                  value={searchState.date_to}
                  onChange={(e) => updateSearchState({ date_to: e.target.value })}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px' : '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <MapPin size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Location
                </label>
                <select
                  value={searchState.location}
                  onChange={(e) => updateSearchState({ location: e.target.value })}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px' : '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">All locations</option>
                  {filterOptions.locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <User size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Assigned To
                </label>
                <select
                  value={searchState.assignee}
                  onChange={(e) => updateSearchState({ assignee: e.target.value })}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px' : '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Anyone</option>
                  <option value="unassigned">Unassigned</option>
                  {filterOptions.assignees.map(person => (
                    <option key={person} value={person}>{person}</option>
                  ))}
                </select>
              </div>

              <div style={{ gridColumn: isMobile ? '1' : 'span 2' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#374151', 
                  marginBottom: '8px' 
                }}>
                  <Settings size={16} style={{ display: 'inline', marginRight: '4px' }} />
                  Equipment Type
                </label>
                <input
                  type="text"
                  placeholder="e.g., Ventilator, X-Ray"
                  value={searchState.equipment_type}
                  onChange={(e) => updateSearchState({ equipment_type: e.target.value })}
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px' : '8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ 
                gridColumn: isMobile ? '1' : 'span 2', 
                display: 'flex', 
                justifyContent: isMobile ? 'center' : 'flex-end', 
                alignItems: 'flex-end' 
              }}>
                <button
                  onClick={clearAllFilters}
                  style={{
                    padding: isMobile ? '12px 24px' : '8px 16px',
                    fontSize: '14px',
                    color: '#6b7280',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  Clear all filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Status Filters */}
      <div style={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? '8px' : '12px', 
        marginBottom: '24px' 
      }}>
        {statusOptions.map(status => (
          <button
            key={status.value}
            onClick={() => handleStatusFilter(status.value)}
            style={{
              padding: isMobile ? '10px 14px' : '8px 16px',
              borderRadius: '6px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              border: '1px solid #d1d5db',
              background: searchState.status === status.value ? '#dbeafe' : 'white',
              color: searchState.status === status.value ? '#1e40af' : '#374151',
              flex: isMobile ? '1 1 calc(50% - 4px)' : 'none',
              justifyContent: 'center',
              minWidth: isMobile ? '0' : 'auto'
            }}
          >
            {status.label}
            <span style={{
              padding: '2px 6px',
              fontSize: isMobile ? '11px' : '12px',
              borderRadius: '12px',
              fontWeight: '700',
              background: searchState.status === status.value ? '#3b82f6' : '#e5e7eb',
              color: searchState.status === status.value ? 'white' : '#6b7280'
            }}>
              {statusCounts[status.key] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Results Summary */}
      <div style={{ 
        background: 'white', 
        borderRadius: '8px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
        border: '1px solid #e5e7eb', 
        padding: '16px', 
        marginBottom: '24px' 
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '12px' : '0'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Showing <span style={{ fontWeight: '600' }}>{((pagination.page - 1) * searchState.limit) + 1}</span> to{' '}
            <span style={{ fontWeight: '600' }}>{Math.min(pagination.page * searchState.limit, pagination.total)}</span> of{' '}
            <span style={{ fontWeight: '600' }}>{pagination.total}</span> results
          </div>
          {!isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Sort by:</span>
              <select 
                value={`${searchState.sort_by}-${searchState.sort_order}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  updateSearchState({ sort_by: field, sort_order: order });
                }}
                style={{
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '4px 12px'
                }}
              >
                <option value="scheduled_time-asc">Due date (earliest first)</option>
                <option value="scheduled_time-desc">Due date (latest first)</option>
                <option value="number-asc">Work order (A-Z)</option>
                <option value="number-desc">Work order (Z-A)</option>
                <option value="work_location-asc">Location (A-Z)</option>
                <option value="risk_level-desc">Risk level (high to low)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Action Buttons */}
      {isMobile && (
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '16px',
          position: 'sticky',
          top: '0',
          zIndex: 10,
          background: '#f9fafb',
          padding: '8px 0'
        }}>
          <button
            onClick={() => setShowUpload(true)}
            style={{ 
              flex: 1,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px', 
              padding: '12px', 
              background: '#2563eb', 
              color: 'white', 
              border: 'none', 
              borderRadius: '6px', 
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            <Upload size={16} />
            Upload
          </button>
          <button style={{ 
            flex: 1,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '8px', 
            padding: '12px', 
            border: '1px solid #d1d5db', 
            borderRadius: '6px', 
            background: 'white', 
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <Download size={16} />
            Export
          </button>
        </div>
      )}

      {/* Results - Mobile Cards or Desktop Table */}
      {isMobile ? (
        <div>
          {loading ? (
            <div style={{ 
              padding: '32px 16px', 
              textAlign: 'center', 
              color: '#6b7280',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              Loading PMs...
            </div>
          ) : pms.length === 0 ? (
            <div style={{ 
              padding: '32px 16px', 
              textAlign: 'center', 
              color: '#6b7280',
              background: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              No PMs found matching your search criteria
            </div>
          ) : (
            pms.map(pm => <PMCard key={pm.id} pm={pm} />)
          )}
        </div>
      ) : (
        // Desktop Table (existing table code unchanged)
        <div style={{ 
          background: 'white', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)', 
          border: '1px solid #e5e7eb', 
          overflow: 'hidden' 
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '14px', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Work Order</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Asset #</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Due Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Equipment</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Location</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Assigned To</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Risk</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Frequency</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Notes</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '500', color: '#374151' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
                      Loading PMs...
                    </td>
                  </tr>
                ) : pms.length === 0 ? (
                  <tr>
                    <td colSpan="11" style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280' }}>
                      No PMs found matching your search criteria
                    </td>
                  </tr>
                ) : (
                  pms.map(pm => (
                    <tr key={pm.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {getStatusIcon(pm.state, pm.complete)}
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: pm.complete === 'YES' ? '#dcfce7' : 
                                       pm.state === 'Work in Progress' ? '#dbeafe' :
                                       pm.state === 'Assigned' ? '#fef3c7' : '#f3f4f6',
                            color: pm.complete === 'YES' ? '#15803d' :
                                  pm.state === 'Work in Progress' ? '#1e40af' :
                                  pm.state === 'Assigned' ? '#d97706' : '#6b7280'
                          }}>
                            {pm.complete === 'YES' ? 'Completed' : pm.state}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '500', color: '#2563eb' }}>{pm.number}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{pm.short_description}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: '500', color: '#2563eb' }}>{pm.asset || 'N/A'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ 
                          fontWeight: '500',
                          color: isOverdue(pm.scheduled_time) && pm.complete !== 'YES' ? '#dc2626' : '#374151'
                        }}>
                          {pm.scheduled_time}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: '500' }}>{pm.type}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{pm['asset.asset_manufacturer']} {pm.model}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div>{pm.work_location}</div>
                        {pm.vicinity && <div style={{ fontSize: '12px', color: '#6b7280' }}>{pm.vicinity}</div>}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{pm.assigned_to || <span style={{ color: '#9ca3af' }}>Unassigned</span>}</td>
                      <td style={{ padding: '12px 16px' }}>
                        {pm.risk_level ? (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: pm.risk_level >= 18 ? '#fef2f2' : pm.risk_level >= 15 ? '#fef3c7' : pm.risk_level >= 10 ? '#dcfce7' : '#f3f4f6',
                            color: pm.risk_level >= 18 ? '#dc2626' : pm.risk_level >= 15 ? '#d97706' : pm.risk_level >= 10 ? '#15803d' : '#6b7280'
                          }}>
                            {pm.risk_level}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {pm.pm_frequency ? (
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: '#eff6ff',
                            color: '#1e40af'
                          }}>
                            {pm.pm_frequency}
                          </span>
                        ) : (
                          <span style={{ color: '#9ca3af', fontSize: '12px' }}>N/A</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <NotesField 
                          pmId={pm.id}
                          initialNotes={pm.notes}
                          onSave={handleUpdateNotes}
                          isMobile={false}
                        />
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {searchState.status === 'completed' && pm.processed !== 'YES' ? (
                          <button
                            onClick={() => processPM(pm.id, pm.number)}
                            disabled={processingPMs.has(pm.id)}
                            style={{
                              padding: '6px 12px',
                              background: processingPMs.has(pm.id) ? '#9ca3af' : '#2563eb',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: processingPMs.has(pm.id) ? 'not-allowed' : 'pointer',
                              opacity: processingPMs.has(pm.id) ? 0.6 : 1
                            }}
                          >
                            {processingPMs.has(pm.id) ? 'Processing...' : 'Process'}
                          </button>
                        ) : searchState.status !== 'processed' && pm.complete !== 'YES' ? (
                          <button
                            onClick={() => openCompleteModal(pm)}
                            disabled={completingPMs.has(pm.id)}
                            style={{
                              padding: '6px 12px',
                              background: completingPMs.has(pm.id) ? '#9ca3af' : '#16a34a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              cursor: completingPMs.has(pm.id) ? 'not-allowed' : 'pointer',
                              opacity: completingPMs.has(pm.id) ? 0.6 : 1
                            }}
                          >
                            {completingPMs.has(pm.id) ? 'Completing...' : 'Complete'}
                          </button>
                        ) : (
                          <span style={{ color: '#16a34a', fontSize: '12px', fontWeight: '500' }}>✓ Done</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div style={{ 
              padding: '16px', 
              borderTop: '1px solid #e5e7eb', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Page {pagination.page} of {pagination.total_pages}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                    opacity: pagination.page === 1 ? 0.5 : 1,
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: pagination.page === pagination.total_pages ? 'not-allowed' : 'pointer',
                    opacity: pagination.page === pagination.total_pages ? 0.5 : 1,
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mobile Pagination */}
      {isMobile && pagination.total_pages > 1 && (
        <div style={{ 
          marginTop: '16px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          padding: '16px',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Page {pagination.page} of {pagination.total_pages}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              style={{
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                cursor: pagination.page === 1 ? 'not-allowed' : 'pointer',
                opacity: pagination.page === 1 ? 0.5 : 1,
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.total_pages}
              style={{
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                cursor: pagination.page === pagination.total_pages ? 'not-allowed' : 'pointer',
                opacity: pagination.page === pagination.total_pages ? 0.5 : 1,
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Mark Complete modal — required adhesive-question step. */}
      {completeModalPm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem'
        }}>
          <div style={{
            background: 'white', borderRadius: '12px', padding: '1.5rem',
            maxWidth: '480px', width: '100%', boxShadow: '0 24px 48px rgba(0,0,0,0.25)'
          }}>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#111827' }}>
              Complete PM #{completeModalPm.number}
            </h3>
            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.35rem', marginBottom: '1rem' }}>
              {completeModalPm.asset ? `Asset ${completeModalPm.asset}` : ''}
              {completeModalPm.type ? ` · ${completeModalPm.type}` : ''}
              {completeModalPm.work_location ? ` · ${completeModalPm.work_location}` : ''}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                Adhesive residue cleaned?
              </div>
              {[
                { value: 'yes', label: 'Yes', hint: 'Residue was present and cleaned' },
                { value: 'no',  label: 'No',  hint: 'Residue was present but not cleaned' },
                { value: 'na',  label: 'N/A', hint: 'Device had no residue' }
              ].map(opt => (
                <label key={opt.value} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  padding: '0.5rem 0.75rem', marginBottom: '0.35rem', borderRadius: '6px',
                  border: adhesiveChoice === opt.value ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  background: adhesiveChoice === opt.value ? '#eff6ff' : 'white',
                  cursor: 'pointer'
                }}>
                  <input
                    type="radio"
                    name="adhesive_cleaned"
                    value={opt.value}
                    checked={adhesiveChoice === opt.value}
                    onChange={() => { setAdhesiveChoice(opt.value); setAdhesivePrompt(false); }}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, color: '#111827' }}>{opt.label}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{opt.hint}</div>
                  </div>
                </label>
              ))}
              {adhesivePrompt && (
                <div style={{
                  marginTop: '0.5rem', padding: '0.5rem 0.75rem',
                  background: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: '6px', color: '#92400e', fontSize: '0.85rem'
                }}>
                  ⚠️ Please answer the adhesive question before completing.
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={closeCompleteModal}
                disabled={completingPMs.has(completeModalPm.id)}
                style={{
                  padding: '0.5rem 1rem', background: 'white', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: '6px',
                  cursor: completingPMs.has(completeModalPm.id) ? 'not-allowed' : 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCompleteModal}
                disabled={completingPMs.has(completeModalPm.id)}
                style={{
                  padding: '0.5rem 1rem', background: '#16a34a', color: 'white',
                  border: 'none', borderRadius: '6px',
                  cursor: completingPMs.has(completeModalPm.id) ? 'not-allowed' : 'pointer',
                  opacity: completingPMs.has(completeModalPm.id) ? 0.6 : 1
                }}
              >
                {completingPMs.has(completeModalPm.id) ? 'Completing...' : 'Confirm Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodShepherdPMs;